import { useEffect, useState, useCallback, useRef } from 'react';

/**
 * F-216 — small fetch-state hook.
 *
 * The review recommended @tanstack/react-query; the agent brief
 * disallows new dependencies. This 40-line replacement covers the
 * common case the workbench actually uses: "fetch once on mount,
 * cancel on unmount, expose loading / error / data / refresh".
 *
 * It does NOT do response caching, retries, mutations, or
 * suspensions. Migrate to react-query for those workflows.
 *
 * Usage:
 *   const { data, loading, error, refresh } = useFetch(() => getStats(), [reloadKey]);
 */
interface UseFetchResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useFetch<T>(
  fetcher: () => Promise<T>,
  deps: React.DependencyList = [],
): UseFetchResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    setLoading(true);
    setError(null);
    fetcher()
      .then((res) => {
        if (cancelledRef.current) return;
        setData(res);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelledRef.current) return;
        setError((err as Error).message || 'Request failed');
        setLoading(false);
      });
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reload]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);
  return { data, loading, error, refresh };
}
